import { Text } from "@chakra-ui/react";
import DeleteConfirmMenuItem, {
  Props as BaseProps,
} from "components/Shared/ConfirmModal/DeleteConfirmMenuItem";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import {
  makeClearNestedListFieldCache,
  removeDeletedRecords,
} from "utils/apollo/cacheHelpers";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { assertPresence } from "utils/assertions";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import BrandName from "components/Shared/BrandName";
import { DocumentVariablesType } from "utils/apollo/hooks";

const fragment = gql(/* GraphQL */ `
  fragment DeleteSkuBrandOrderingPlatformMenuItem on SkuBrandOrderingPlatform {
    id
    analyticsSyncDetails {
      isUpdatedBySync
    }
    sku {
      id
      fullName
    }
    brandOrderingPlatform {
      id
      orderingPlatform {
        id
        name
      }
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation DeleteSkuBrandOrderingPlatform($skuBrandOrderingPlatformId: UUID!) {
    deleteSkuBrandOrderingPlatform(input: { id: $skuBrandOrderingPlatformId }) {
      skuBrandOrderingPlatform {
        id
        sku {
          id
          fullName
        }
        brandOrderingPlatform {
          id
          orderingPlatform {
            id
            name
          }
          brand {
            id
            name
          }
        }
      }
    }
  }
`);

type Props = Omit<
  BaseProps<
    DocumentType<typeof mutation>,
    DocumentVariablesType<typeof mutation>
  >,
  "mutation"
> & {
  onCompleted?: () => void;
  skuBrandOrderingPlatform: FragmentType<typeof fragment> | null | undefined;
};

export default function DeleteMenuItem({
  onCompleted,
  skuBrandOrderingPlatform,
  ...rest
}: Props) {
  const sbop = getFragmentData(fragment, skuBrandOrderingPlatform);

  const { showSuccessToast } = useAsyncCompleteToast();

  const mutationTuple = useBrandMutation(mutation, {
    variables: sbop?.id ? { skuBrandOrderingPlatformId: sbop.id } : undefined,
    update: (cache, result) => {
      const deletedSbop =
        result.data?.deleteSkuBrandOrderingPlatform?.skuBrandOrderingPlatform;

      assertPresence(deletedSbop);

      const cacheId = cache.identify(deletedSbop);
      cache.evict({ id: cacheId, broadcast: true });
      cache.gc();

      removeDeletedRecords({
        cache,
        baseFieldName: "skuBrandOrderingPlatforms",
        deletedRecords: [deletedSbop],
      });

      const clearNestedListFieldCache = makeClearNestedListFieldCache(cache);
      clearNestedListFieldCache("Sku", [
        "skuBrandOrderingPlatforms",
        "skuBrandDistributionCenters",
      ]);
      clearNestedListFieldCache("BrandOrderingPlatform", [
        "skuBrandOrderingPlatforms",
      ]);
      clearNestedListFieldCache("BrandDistributionCenter", [
        "skuBrandDistributionCenters",
      ]);

      cache.gc();
    },
    onCompleted: (data) => {
      const deletedSbop =
        data.deleteSkuBrandOrderingPlatform?.skuBrandOrderingPlatform;
      assertPresence(deletedSbop);
      assertPresence(deletedSbop.sku);
      assertPresence(deletedSbop.brandOrderingPlatform?.orderingPlatform);
      assertPresence(deletedSbop.brandOrderingPlatform.brand);
      const deletedSkuName = deletedSbop.sku.fullName;
      const deletedOpName =
        deletedSbop.brandOrderingPlatform.orderingPlatform.name;
      const deletedBrandName = deletedSbop.brandOrderingPlatform.brand.name;
      showSuccessToast(
        `Successfully removed “${deletedSkuName}” from “${deletedOpName}” for ${deletedBrandName}`,
      );
      onCompleted?.();
    },
  });

  const skuName = sbop?.sku?.fullName;
  const opName = sbop?.brandOrderingPlatform?.orderingPlatform?.name;

  return (
    <DeleteConfirmMenuItem
      label="Remove Product"
      mutation={mutationTuple}
      menuItemProps={{
        isDisabled: !sbop?.id || sbop.analyticsSyncDetails?.isUpdatedBySync,
      }}
      disabledTooltip={
        sbop?.analyticsSyncDetails?.isUpdatedBySync
          ? "This product is synced from Analytics and cannot be removed"
          : undefined
      }
      confirmText={
        <Text>
          Are you sure you want to remove{" "}
          {skuName ? <b>{skuName}</b> : "this product"} from{" "}
          {opName ? <b>{opName}</b> : "this customer"} for{" "}
          <b>
            <BrandName />
          </b>
          ?
        </Text>
      }
      {...rest}
    />
  );
}
